// ABOUTME: Decides whether an e-mail reference is copied or updated for a form save.
// ABOUTME: Records ownership needed by job retries without persisting form-only state.

import {
    buildClientRequestFingerprint,
    generateClientRequestId
} from "../../../lib/clientRequestId";

export type EmailPersistenceMode = "update" | "job-create" | "job-edit";

const getEmailReferenceTarget = (
    fieldItem: any,
    persistenceMode: EmailPersistenceMode
) => {
    const currentId = fieldItem.id;
    const tempData = fieldItem._tempEmailData;
    const writeReference: Compleo.IObject = {};

    if (
        currentId &&
        persistenceMode === "job-create" &&
        !tempData.persistedForJob
    ) {
        writeReference.sourceId = currentId;
    } else if (
        currentId &&
        persistenceMode === "job-edit" &&
        !fieldItem.jobSpecific
    ) {
        writeReference.sourceId = currentId;
    } else if (currentId) {
        writeReference.id = currentId;
    }

    if (persistenceMode !== "update") {
        writeReference.copyAttachments = true;
    }

    return writeReference;
};

const getEmailCopyFingerprint = (
    requestData: Compleo.IObject,
    writeReference: Compleo.IObject
) =>
    buildClientRequestFingerprint({
        ...requestData,
        ...writeReference
    });

export const getPendingEmailCopyRequest = (
    fieldItem: any,
    persistenceMode: EmailPersistenceMode,
    requestData: Compleo.IObject
) => {
    const tempData = fieldItem._tempEmailData;
    const pendingRequest = tempData.emailCopyRequestPayload;
    if (!pendingRequest) {
        return undefined;
    }
    const writeReference = getEmailReferenceTarget(fieldItem, persistenceMode);
    if (writeReference.id) {
        return undefined;
    }
    const fingerprint = getEmailCopyFingerprint(requestData, writeReference);
    return tempData.emailCopyRequestFingerprint === fingerprint
        ? undefined
        : pendingRequest;
};

export const getEmailReferenceWrite = (
    fieldItem: any,
    persistenceMode: EmailPersistenceMode,
    requestData: Compleo.IObject = {}
) => {
    const tempData = fieldItem._tempEmailData;
    const writeReference = getEmailReferenceTarget(fieldItem, persistenceMode);
    if (persistenceMode !== "update" && !writeReference.id) {
        const fingerprint = getEmailCopyFingerprint(
            requestData,
            writeReference
        );
        if (!tempData.emailCopyRequestId) {
            tempData.emailCopyRequestId = generateClientRequestId();
            tempData.emailCopyRequestFingerprint = fingerprint;
        } else if (tempData.emailCopyRequestFingerprint !== fingerprint) {
            throw new Error(
                "Pending e-mail persistence must be recovered before applying edits"
            );
        }
        writeReference.clientRequestId = tempData.emailCopyRequestId;
        tempData.emailCopyRequestPayload = {
            ...requestData,
            ...writeReference
        };
    }

    return writeReference;
};

export const applyEmailReferenceWrite = (
    fieldItem: any,
    persistenceMode: EmailPersistenceMode,
    id: string
) => {
    if (!id && persistenceMode !== "update") {
        throw new Error("E-mail persistence response is missing an id");
    }
    fieldItem.id = id;
    if (persistenceMode !== "update") {
        fieldItem.jobSpecific = true;
        fieldItem._tempEmailData.persistedForJob = true;
        delete fieldItem._tempEmailData.emailCopyRequestId;
        delete fieldItem._tempEmailData.emailCopyRequestFingerprint;
        delete fieldItem._tempEmailData.emailCopyRequestPayload;
    }
};
